import React, { useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Grid, Typography } from '@mui/material';
import { IconButton, Menu, MenuItem } from '@biq/apex-ui';
import { cancelJobMenuStyles } from './cancel-job-menu-helper';
import CancelIcon from '@mui/icons-material/Cancel';
import { ConfirmationModal } from 'components';
import { styles as confirmationModalStyles } from 'components/confirmation-modal/confirmation-modal-helper';
import { useCancelJob } from 'hooks';
import { useAuth } from '../../../../core';

interface CancelJobMenuProps {
    jobId: string;
    refreshJobList: Function;
}

export const CancelJobMenu = ({
    jobId,
    refreshJobList,
}: CancelJobMenuProps): React.JSX.Element => {
    const { getToken } = useAuth();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const { mutate: cancelJob } = useCancelJob();
    const open: boolean = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (): void => {
        setAnchorEl(null);
    };

    const closeDeleteModal = (): void => {
        setOpenDeleteModal(false);
        setAnchorEl(null);
    };

    const handleCancelJobClick = (): void => {
        setOpenDeleteModal(true);
    };

    const handleCancel = (): void => {
        setTimeout(() => {
            refreshJobList();
        }, 500);
        cancelJob({ jobId: jobId, token: getToken() });
        closeDeleteModal();
    };

    return (
        <Grid>
            <IconButton
                aria-label='more'
                id='cancel-job-button'
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup='true'
                onClick={handleClick}
                sx={cancelJobMenuStyles.iconStyles}
            >
                <MoreVertIcon />
            </IconButton>
            <Menu
                id='cancel-job-button'
                MenuListProps={{
                    'aria-labelledby': 'cancel-job-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                sx={cancelJobMenuStyles.menuStyles}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleCancelJobClick}>
                    <Box sx={cancelJobMenuStyles.menuItemContainerStyles}>
                        <Box display={'flex'}>
                            <CancelIcon
                                sx={cancelJobMenuStyles.cancelIconStyles}
                            />
                        </Box>
                        <Typography>Cancel Job</Typography>
                    </Box>
                </MenuItem>
            </Menu>
            {openDeleteModal && (
                <ConfirmationModal
                    confirmationModal={openDeleteModal}
                    handleConfirmationModal={closeDeleteModal}
                    confirmationKey={'CANCEL'}
                    confirmationButtonText={'Cancel Job'}
                    confirmationCancelButtonText={'Go Back'}
                    confirmationHeadingText={'Confirm Job Cancelation'}
                    confirmationInputLabelText={'Type CANCEL below'}
                    handleConfirmation={handleCancel}
                    confirmationCancelButtonArrowIcon={true}
                    confirmationSubHeadingText={
                        <Typography>
                            By typing{' '}
                            <Typography
                                component='span'
                                sx={confirmationModalStyles.textBoldStyles}
                            >
                                ‘CANCEL’
                            </Typography>{' '}
                            below, you understand that you are permanently
                            canceling this job. This action{' '}
                            <Typography
                                component='span'
                                sx={confirmationModalStyles.textBoldStyles}
                            >
                                CANNOT
                            </Typography>{' '}
                            be undone.
                        </Typography>
                    }
                />
            )}
        </Grid>
    );
};
